import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiBookOpen,
    FiCreditCard,
    FiFileText,
    FiLoader,
    FiMail,
    FiPhone,
    FiRefreshCw,
    FiSearch,
    FiTrendingDown,
    FiX,
} from "react-icons/fi";

import API from "../../../../../../../utils/apiCalling";

import {
    config,
} from "../../../../../../../utils/apiUrl";

import {
    calculateLedgerSummary,
    formatCurrency,
    formatDate,
    formatType,
    getBalance,
    getCredit,
    getDebit,
    getDescription,
    getEntryDate,
    getEntryType,
    getReference,
    normalizeLedgerResponse,
    normalizeText,
} from "../utils/customerLedgerUtils";

const api =
    new API();

/*
|--------------------------------------------------------------------------
| ENTRY ICON
|--------------------------------------------------------------------------
*/

const EntryIcon = ({
    type,
}) => {
    if (
        type.includes(
            "INVOICE"
        )
    ) {
        return (
            <FiFileText />
        );
    }

    if (
        type.includes(
            "PAYMENT"
        )
    ) {
        return (
            <FiCreditCard />
        );
    }

    if (
        type.includes(
            "CREDIT"
        )
    ) {
        return (
            <FiTrendingDown />
        );
    }

    return (
        <FiBookOpen />
    );
};

/*
|--------------------------------------------------------------------------
| GET ERROR MESSAGE
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error
) => {
    return (
        error?.response?.data
            ?.message ||
        error?.response?.data
            ?.error ||
        error?.message ||
        "Unable to load customer ledger."
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const CustomerLedgerDialog = ({
    open,
    customer,
    onClose,
}) => {
    /*
    |--------------------------------------------------------------------------
    | DATA
    |--------------------------------------------------------------------------
    */

    const [
        customerEntries,
        setCustomerEntries,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(false);

    const [
        error,
        setError,
    ] = useState("");

    /*
    |--------------------------------------------------------------------------
    | FILTERS
    |--------------------------------------------------------------------------
    */

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        entryType,
        setEntryType,
    ] = useState("");

    /*
    |--------------------------------------------------------------------------
    | LOAD COMPLETE CUSTOMER LEDGER
    |--------------------------------------------------------------------------
    */

    const loadCustomerLedger =
        useCallback(
            async () => {
                if (
                    !open ||
                    !customer
                ) {
                    return;
                }

                try {
                    setLoading(
                        true
                    );

                    setError(
                        ""
                    );

                    let response =
                        null;

                    /*
                    |--------------------------------------------------------------------------
                    | OPTION 1 - CUSTOMER ID
                    |--------------------------------------------------------------------------
                    */

                    if (
                        customer
                            ?.customerId
                    ) {
                        response =
                            await api.get(
                                config.getCustomerLedgerByCustomer(
                                    customer
                                        .customerId
                                )
                            );
                    }

                    /*
                    |--------------------------------------------------------------------------
                    | OPTION 2 - MOBILE FALLBACK
                    |--------------------------------------------------------------------------
                    */

                    else if (
                        customer
                            ?.mobile &&
                        customer
                            .mobile !==
                            "-"
                    ) {
                        response =
                            await api.get(
                                config.getCustomerLedgerByMobile(
                                    customer
                                        .mobile
                                )
                            );
                    }

                    /*
                    |--------------------------------------------------------------------------
                    | NO CUSTOMER IDENTIFIER
                    |--------------------------------------------------------------------------
                    */

                    else {
                        setCustomerEntries(
                            Array.isArray(
                                customer
                                    ?.entries
                            )
                                ? customer.entries
                                : []
                        );

                        return;
                    }

                    const normalized =
                        normalizeLedgerResponse(
                            response
                        );

                    const rows =
                        Array.isArray(
                            normalized
                                ?.records
                        )
                            ? normalized.records
                            : [];

                    setCustomerEntries(
                        rows
                    );
                } catch (
                    requestError
                ) {
                    console.error(
                        "Customer ledger details error:",
                        requestError
                    );

                    /*
                     * Use already available records as fallback.
                     */
                    setCustomerEntries(
                        Array.isArray(
                            customer
                                ?.entries
                        )
                            ? customer.entries
                            : []
                    );

                    setError(
                        getErrorMessage(
                            requestError
                        )
                    );
                } finally {
                    setLoading(
                        false
                    );
                }
            },
            [
                open,
                customer,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | OPEN / RESET
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (
                !open
            ) {
                return;
            }

            setSearch(
                ""
            );

            setEntryType(
                ""
            );

            setError(
                ""
            );

            setCustomerEntries(
                []
            );

            loadCustomerLedger();
        },
        [
            open,
            customer,
            loadCustomerLedger,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | FILTER ENTRIES
    |--------------------------------------------------------------------------
    */

    const filteredEntries =
        useMemo(
            () => {
                const keyword =
                    normalizeText(
                        search
                    );

                return customerEntries.filter(
                    (
                        row
                    ) => {
                        const type =
                            getEntryType(
                                row
                            );

                        if (
                            entryType &&
                            type !==
                                entryType
                        ) {
                            return false;
                        }

                        if (
                            !keyword
                        ) {
                            return true;
                        }

                        const searchableText =
                            [
                                getReference(
                                    row
                                ),

                                getDescription(
                                    row
                                ),

                                type,
                            ]
                                .filter(
                                    Boolean
                                )
                                .join(
                                    " "
                                )
                                .toLowerCase();

                        return searchableText.includes(
                            keyword
                        );
                    }
                );
            },
            [
                customerEntries,
                search,
                entryType,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | REAL CUSTOMER SUMMARY
    |--------------------------------------------------------------------------
    */

    const customerSummary =
        useMemo(
            () => {
                if (
                    customerEntries.length >
                    0
                ) {
                    return calculateLedgerSummary(
                        customerEntries
                    );
                }

                return {
                    totalDebit:
                        Number(
                            customer
                                ?.totalDebit ||
                                0
                        ),

                    totalCredit:
                        Number(
                            customer
                                ?.totalCredit ||
                                0
                        ),

                    netBalance:
                        Number(
                            customer
                                ?.balance ||
                                0
                        ),
                };
            },
            [
                customerEntries,
                customer,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | CURRENT BALANCE
    |--------------------------------------------------------------------------
    */

    const currentBalance =
        useMemo(
            () => {
                if (
                    customerEntries.length >
                    0
                ) {
                    const sorted =
                        [
                            ...customerEntries,
                        ].sort(
                            (
                                first,
                                second
                            ) =>
                                new Date(
                                    getEntryDate(
                                        second
                                    ) ||
                                        0
                                ) -
                                new Date(
                                    getEntryDate(
                                        first
                                    ) ||
                                        0
                                )
                        );

                    const latest =
                        sorted[0];

                    const latestBalance =
                        latest
                            ? getBalance(
                                  latest
                              )
                            : 0;

                    /*
                     * Prefer stored running balance.
                     */
                    if (
                        latestBalance !==
                        0
                    ) {
                        return latestBalance;
                    }
                }

                return customerSummary
                    .netBalance;
            },
            [
                customerEntries,
                customerSummary,
            ]
        );

    const hasOutstanding =
        currentBalance >
        0;

    /*
    |--------------------------------------------------------------------------
    | LAST ACTIVITY
    |--------------------------------------------------------------------------
    */

    const lastActivity =
        useMemo(
            () => {
                if (
                    customerEntries.length ===
                    0
                ) {
                    return customer
                        ?.lastActivity;
                }

                return customerEntries.reduce(
                    (
                        latest,
                        row
                    ) => {
                        const date =
                            getEntryDate(
                                row
                            );

                        if (
                            !date
                        ) {
                            return latest;
                        }

                        if (
                            !latest ||
                            new Date(
                                date
                            ) >
                                new Date(
                                    latest
                                )
                        ) {
                            return date;
                        }

                        return latest;
                    },
                    null
                );
            },
            [
                customerEntries,
                customer,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | CLOSE
    |--------------------------------------------------------------------------
    */

    const handleClose =
        () => {
            setSearch(
                ""
            );

            setEntryType(
                ""
            );

            setError(
                ""
            );

            setCustomerEntries(
                []
            );

            onClose?.();
        };

    if (
        !open ||
        !customer
    ) {
        return null;
    }

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="customer-ledger-dialog-overlay">
            <div
                className="customer-ledger-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="customer-ledger-dialog-title"
            >
                {/* ======================================================
                    HEADER
                ====================================================== */}

                <header className="customer-ledger-dialog-header">
                    <div className="customer-ledger-dialog-profile">
                        <span className="customer-ledger-dialog-avatar">
                            {String(
                                customer
                                    ?.name ||
                                    "C"
                            )
                                .charAt(
                                    0
                                )
                                .toUpperCase()}
                        </span>

                        <div>
                            <span className="customer-ledger-dialog-eyebrow">
                                Customer Ledger
                            </span>

                            <h2 id="customer-ledger-dialog-title">
                                {customer
                                    ?.name ||
                                    "Walk-in Customer"}
                            </h2>

                            <div className="customer-ledger-dialog-contact">
                                <span>
                                    <FiPhone />

                                    {customer
                                        ?.mobile ||
                                        "-"}
                                </span>

                                {customer
                                    ?.email && (
                                    <span>
                                        <FiMail />

                                        {
                                            customer
                                                .email
                                        }
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="customer-ledger-dialog-close"
                        onClick={
                            handleClose
                        }
                        aria-label="Close"
                    >
                        <FiX />
                    </button>
                </header>

                {/* ======================================================
                    BODY
                ====================================================== */}

                <div className="customer-ledger-dialog-body">
                    {/* SUMMARY */}

                    <section className="customer-ledger-dialog-summary">
                        <div>
                            <span>
                                Transactions
                            </span>

                            <strong>
                                {
                                    customerEntries.length
                                }
                            </strong>

                            <small>
                                Ledger entries
                            </small>
                        </div>

                        <div className="customer-ledger-dialog-summary-debit">
                            <span>
                                Total Debit
                            </span>

                            <strong>
                                {formatCurrency(
                                    customerSummary
                                        .totalDebit
                                )}
                            </strong>

                            <small>
                                Sales / receivable
                            </small>
                        </div>

                        <div className="customer-ledger-dialog-summary-credit">
                            <span>
                                Total Credit
                            </span>

                            <strong>
                                {formatCurrency(
                                    customerSummary
                                        .totalCredit
                                )}
                            </strong>

                            <small>
                                Payments / credits
                            </small>
                        </div>

                        <div
                            className={
                                hasOutstanding
                                    ? "customer-ledger-dialog-summary-balance customer-ledger-dialog-summary-balance-due"
                                    : "customer-ledger-dialog-summary-balance customer-ledger-dialog-summary-balance-clear"
                            }
                        >
                            <span>
                                Current Balance
                            </span>

                            <strong>
                                {formatCurrency(
                                    currentBalance
                                )}
                            </strong>

                            <small>
                                {hasOutstanding
                                    ? "Outstanding"
                                    : "Account clear"}
                            </small>
                        </div>
                    </section>

                    {/* ERROR */}

                    {error && (
                        <div className="customer-ledger-dialog-error">
                            <FiAlertCircle />

                            <div>
                                <strong>
                                    Unable to refresh complete ledger
                                </strong>

                                <span>
                                    {error}
                                </span>

                                {customerEntries.length >
                                    0 && (
                                    <small>
                                        Showing locally available transactions.
                                    </small>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TOOLBAR */}

                    <section className="customer-ledger-dialog-toolbar">
                        <div className="customer-ledger-dialog-search">
                            <FiSearch />

                            <input
                                type="text"
                                value={
                                    search
                                }
                                placeholder="Search invoice, payment, reference or description..."
                                onChange={(
                                    event
                                ) =>
                                    setSearch(
                                        event
                                            .target
                                            .value
                                    )
                                }
                            />

                            {search && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setSearch(
                                            ""
                                        )
                                    }
                                >
                                    <FiX />
                                </button>
                            )}
                        </div>

                        <select
                            value={
                                entryType
                            }
                            onChange={(
                                event
                            ) =>
                                setEntryType(
                                    event
                                        .target
                                        .value
                                )
                            }
                        >
                            <option value="">
                                All Transactions
                            </option>

                            <option value="SALE_INVOICE">
                                Sale Invoice
                            </option>

                            <option value="INVOICE">
                                Invoice
                            </option>

                            <option value="PAYMENT">
                                Payment
                            </option>

                            <option value="CREDIT_NOTE">
                                Credit Note
                            </option>

                            <option value="ADJUSTMENT">
                                Adjustment
                            </option>

                            <option value="REVERSAL">
                                Reversal
                            </option>

                            <option value="PAYMENT_REVERSAL">
                                Payment Reversal
                            </option>
                        </select>
                    </section>

                    {/* TABLE */}

                    <section className="customer-ledger-dialog-table-card">
                        {loading ? (
                            <div className="customer-ledger-dialog-loading">
                                <FiLoader className="customer-ledger-spinner" />

                                <strong>
                                    Loading customer ledger...
                                </strong>

                                <span>
                                    Fetching complete transaction history.
                                </span>
                            </div>
                        ) : filteredEntries.length ===
                          0 ? (
                            <div className="customer-ledger-dialog-empty">
                                <FiBookOpen />

                                <h3>
                                    No ledger entries found
                                </h3>

                                <p>
                                    {search ||
                                    entryType
                                        ? "Try changing your search or transaction filter."
                                        : "No ledger transactions are available for this customer."}
                                </p>
                            </div>
                        ) : (
                            <div className="customer-ledger-table-wrapper">
                                <table className="customer-ledger-table customer-ledger-dialog-table">
                                    <thead>
                                        <tr>
                                            <th>
                                                Date
                                            </th>

                                            <th>
                                                Transaction
                                            </th>

                                            <th>
                                                Reference
                                            </th>

                                            <th>
                                                Description
                                            </th>

                                            <th className="text-right">
                                                Debit
                                            </th>

                                            <th className="text-right">
                                                Credit
                                            </th>

                                            <th className="text-right">
                                                Balance
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredEntries.map(
                                            (
                                                row,
                                                index
                                            ) => {
                                                const type =
                                                    getEntryType(
                                                        row
                                                    );

                                                const debit =
                                                    getDebit(
                                                        row
                                                    );

                                                const credit =
                                                    getCredit(
                                                        row
                                                    );

                                                return (
                                                    <tr
                                                        key={
                                                            row?._id ||
                                                            row?.id ||
                                                            `${type}-${index}`
                                                        }
                                                    >
                                                        <td>
                                                            <span className="customer-ledger-date">
                                                                {formatDate(
                                                                    getEntryDate(
                                                                        row
                                                                    )
                                                                )}
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <span
                                                                className={`customer-ledger-type customer-ledger-type-${type
                                                                    .toLowerCase()
                                                                    .replace(
                                                                        /_/g,
                                                                        "-"
                                                                    )}`}
                                                            >
                                                                <EntryIcon
                                                                    type={
                                                                        type
                                                                    }
                                                                />

                                                                {formatType(
                                                                    type
                                                                )}
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <strong className="customer-ledger-reference">
                                                                {getReference(
                                                                    row
                                                                )}
                                                            </strong>
                                                        </td>

                                                        <td>
                                                            <span className="customer-ledger-description">
                                                                {getDescription(
                                                                    row
                                                                )}
                                                            </span>
                                                        </td>

                                                        <td className="text-right">
                                                            <strong className="customer-ledger-debit">
                                                                {debit >
                                                                0
                                                                    ? formatCurrency(
                                                                          debit
                                                                      )
                                                                    : "-"}
                                                            </strong>
                                                        </td>

                                                        <td className="text-right">
                                                            <strong className="customer-ledger-credit">
                                                                {credit >
                                                                0
                                                                    ? formatCurrency(
                                                                          credit
                                                                      )
                                                                    : "-"}
                                                            </strong>
                                                        </td>

                                                        <td className="text-right">
                                                            <strong className="customer-ledger-balance">
                                                                {formatCurrency(
                                                                    getBalance(
                                                                        row
                                                                    )
                                                                )}
                                                            </strong>
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </div>

                {/* ======================================================
                    FOOTER
                ====================================================== */}

                <footer className="customer-ledger-dialog-footer">
                    <div>
                        <span>
                            Last Activity
                        </span>

                        <strong>
                            {formatDate(
                                lastActivity
                            )}
                        </strong>
                    </div>

                    <div className="customer-ledger-dialog-footer-actions">
                        <button
                            type="button"
                            className="customer-ledger-dialog-refresh-button"
                            disabled={
                                loading
                            }
                            onClick={
                                loadCustomerLedger
                            }
                        >
                            <FiRefreshCw
                                className={
                                    loading
                                        ? "customer-ledger-spinner"
                                        : ""
                                }
                            />

                            Refresh
                        </button>

                        <button
                            type="button"
                            className="customer-ledger-dialog-close-button"
                            disabled={
                                loading
                            }
                            onClick={
                                handleClose
                            }
                        >
                            Close
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default CustomerLedgerDialog;